import { useState } from "react";
import { QRCodeSVG } from "qrcode.react";
import { toast } from "sonner";
import { AlertTriangle, Loader2, ShieldCheck } from "lucide-react";
import { api, errMsg } from "@/lib/api";
import { inr } from "@/lib/format";
import { useConfig } from "@/hooks/useConfig";

export default function PaymentPanel({ booking, token, onUpdated }) {
  const cfg = useConfig();
  const [utr, setUtr] = useState("");
  const [busy, setBusy] = useState(false);
  const upiUrl = `upi://pay?pa=${encodeURIComponent(cfg.upi_id || "")}&pn=${encodeURIComponent(cfg.payee_name || "The Ananta Hotels")}&am=${booking.total}.00&cu=INR&tn=${booking.booking_id}`;

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = await api.post(`/bookings/${booking.booking_id}/payment`, { token, utr });
      toast.success("Payment reference submitted. We will verify it shortly.");
      onUpdated(data);
    } catch (err) {
      toast.error(errMsg(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <section className="bg-white border border-[#E2DCD0] p-6 sm:p-8" data-testid="payment-panel">
      <p className="eyebrow text-[#C28B5E]">Secure UPI payment</p>
      <h2 className="mt-1 text-3xl text-[#1E3B2E]">Pay {inr(booking.total)}</h2>
      <div className="mt-6 grid sm:grid-cols-2 gap-8 items-start">
        <div className="flex flex-col items-center border border-[#E2DCD0] p-5 bg-[#FDFBF7]">
          {cfg.qr_image ? (
            <>
              <img src={cfg.qr_image} alt="Payment QR code" className="w-[200px] h-[200px] object-contain" data-testid="payment-qr-image" />
              {cfg.upi_id && <p className="mt-4 text-sm font-medium" data-testid="payment-upi-id">{cfg.upi_id}</p>}
              <p className="text-xs text-[#6b6b6b]">{cfg.payee_name}</p>
              <p className="text-xs mt-2 text-center">Enter amount <strong>{inr(booking.total)}</strong> in your UPI app</p>
            </>
          ) : cfg.upi_configured ? (
            <>
              <QRCodeSVG value={upiUrl} size={200} fgColor="#1E3B2E" data-testid="payment-qr" />
              <p className="mt-4 text-sm font-medium" data-testid="payment-upi-id">{cfg.upi_id}</p>
              <p className="text-xs text-[#6b6b6b]">{cfg.payee_name}</p>
              <a href={upiUrl} className="btn-primary mt-4 w-full sm:hidden" data-testid="payment-open-upi-app-btn">Open UPI app</a>
            </>
          ) : (
            <div className="text-center py-8" data-testid="payment-upi-not-configured">
              <AlertTriangle className="mx-auto text-[#C28B5E]" />
              <p className="mt-3 text-sm">Online UPI payment is being set up.</p>
              <p className="text-xs text-[#6b6b6b] mt-1">Please call or WhatsApp {cfg.contact_phone} to complete payment for booking {booking.booking_id}.</p>
            </div>
          )}
        </div>
        {cfg.upi_configured && <div className="text-sm space-y-3">
          <ol className="list-decimal pl-5 space-y-2 text-[#3b3b3b]">
            <li>Scan the QR with any UPI app (GPay, PhonePe, Paytm, BHIM).</li>
            <li>Check the payee name and amount <strong>{inr(booking.total)}</strong> before paying.</li>
            <li>Enter the UTR / transaction reference from your UPI app below.</li>
          </ol>
          {cfg.payment_note && <p className="text-xs text-[#3b3b3b] whitespace-pre-line" data-testid="payment-note">{cfg.payment_note}</p>}
          <form onSubmit={submit} className="pt-3 space-y-3">
            <input className="field uppercase tracking-wider" placeholder="UTR / Transaction ID" value={utr} onChange={(e) => setUtr(e.target.value.trim())} required minLength={8} maxLength={22} data-testid="payment-utr-input" />
            <button className="btn-primary w-full" disabled={busy || utr.length < 8} data-testid="payment-submit-utr-btn">{busy ? <Loader2 size={16} className="animate-spin" /> : "Submit payment reference"}</button>
          </form>
          <p className="flex items-center gap-2 text-xs text-[#6b6b6b]"><ShieldCheck size={13} /> Pay only to the UPI ID shown here. We never ask for your UPI PIN or card details.</p>
        </div>}
      </div>
    </section>
  );
}
